import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { syncNativeChrome } from "@/utils/themeNative";

const ThemeContext = createContext({ isDark: true, toggle: () => {}, applyServerPreference: () => {} });

export const useTheme = () => useContext(ThemeContext);

export function ThemeProvider({ children }) {
  // DARK is the default: light only if the user explicitly chose it.
  // v2 key: legacy "ade_theme" is ignored (old versions wrote "light" automatically).
  const [isDark, setIsDark] = useState(() => {
    return localStorage.getItem("ade_theme_v2") !== "light";
  });

  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add("dark");
      root.setAttribute("data-theme", "dark");
      localStorage.setItem("ade_theme_v2", "dark");
    } else {
      root.classList.remove("dark");
      root.setAttribute("data-theme", "light");
      localStorage.setItem("ade_theme_v2", "light");
    }
    syncNativeChrome(isDark);
  }, [isDark]);

  // Re-apply native status bar when the app returns to foreground (iOS/Capacitor)
  useEffect(() => {
    let handle;
    (async () => {
      try {
        const { Capacitor } = await import("@capacitor/core");
        if (!Capacitor.isNativePlatform()) return;
        const { App: CapApp } = await import("@capacitor/app");
        handle = await CapApp.addListener("resume", () => {
          syncNativeChrome(localStorage.getItem("ade_theme_v2") !== "light");
        });
      } catch (e) { /* not native */ }
    })();
    return () => { handle?.remove(); };
  }, []);

  const toggle = () => setIsDark((prev) => !prev);

  // Server-side preference (user.settings.theme_preference) wins over local default
  const applyServerPreference = useCallback((pref) => {
    if (pref === "light") setIsDark(false);
    else if (pref === "dark") setIsDark(true);
  }, []);

  return (
    <ThemeContext.Provider value={{ isDark, toggle, applyServerPreference }}>
      {children}
    </ThemeContext.Provider>
  );
}
